import { z } from 'zod'

import {
  callerNeedSchema,
  handoffSummarySchema,
  informationResultSchema,
  interactionIntentSchema,
} from './conversations-contracts'

const positiveFinite = z.number().finite().positive()
const nonnegativeFinite = z.number().finite().nonnegative()
const callRef = z.string().regex(/^[0-9a-f]{64}$/)
const aggregateId = z.string().regex(/^ia_v1_[0-9a-f]{64}$/)

// One call's conversation facts (the call projection's timeline child). Enums,
// times and opaque refs only.
const callTimelineSchema = z
  .object({
    schemaVersion: z.literal(1),
    intent: interactionIntentSchema.optional(),
    secondary: z.array(interactionIntentSchema).max(9).optional(),
    topics: z.array(z.string().max(40)).max(8).optional(),
    ...handoffSummarySchema.shape,
    information: informationResultSchema.optional(),
    requestPassed: z.literal(true).optional(),
    need: callerNeedSchema.optional(),
    releasedAtMs: positiveFinite.optional(),
    returnedAtMs: positiveFinite.optional(),
    connectedSeconds: nonnegativeFinite.optional(),
    returnOfCallRef: callRef.optional(),
    returnedToCallRef: callRef.optional(),
  })
  .strict()

export const interactionCallSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId,
    callRef,
    startedAtMs: positiveFinite,
    endedAtMs: positiveFinite,
    durationSeconds: nonnegativeFinite,
    direction: z.enum(['INCOMING', 'OUTGOING', 'UNKNOWN']),
    status: z.literal('ENDED'),
    endReason: z.enum(['CALLER_ENDED', 'ASSISTANT_ENDED']).nullable(),
    recordingAvailable: z.boolean(),
    transcriptAvailable: z.boolean(),
    qualityAnalysisAvailable: z.boolean(),
    summary: z.string().trim().min(1).max(500).nullable(),
    summaryAuthority: z.literal('PROVIDER_DERIVED'),
    classification: z.literal('CALLER_MESSAGE').optional(),
    savedMessage: z.string().min(1).max(4012).optional(),
    messageRevision: z.number().int().min(1).max(20).optional(),
    timeline: callTimelineSchema.optional(),
  })
  .strict()
  .refine((call) => call.endedAtMs >= call.startedAtMs)
  .refine((call) =>
    call.classification === 'CALLER_MESSAGE'
      ? call.savedMessage !== undefined && call.messageRevision !== undefined
      : call.savedMessage === undefined && call.messageRevision === undefined
  )

export const interactionCallPageSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId,
    calls: z.array(interactionCallSchema).max(25),
    nextCursor: z.string().min(1).max(512).nullable(),
  })
  .strict()

export const callTranscriptSchema = z
  .object({
    schemaVersion: z.literal(1),
    callRef,
    startedAtMs: positiveFinite.optional(),
    durationMs: nonnegativeFinite.optional(),
    segments: z
      .array(
        z
          .object({
            speaker: z.enum(['PATIENT', 'AI']),
            // A line played to the front desk during a transfer: the caller
            // never heard it, so it is never shown as what was said to them.
            audience: z.literal('FRONT_DESK').optional(),
            text: z.string().min(1).max(4_000),
            startedAtMs: positiveFinite.optional(),
            endedAtMs: positiveFinite.optional(),
            offsetMs: nonnegativeFinite.optional(),
          })
          .strict()
      )
      .max(300),
    // What the owners did during the call, to read in place among the words.
    events: z
      .array(
        z
          .object({
            kind: z.enum([
              'FRONT_DESK',
              'INFORMATION',
              'CALLBACK',
              'MESSAGE',
              'REQUEST',
            ]),
            result: z.string().max(40).optional(),
            reason: z.string().max(40).optional(),
            topic: z.string().max(40).optional(),
            available: z.boolean().optional(),
            request: z.string().max(40).optional(),
            offsetMs: nonnegativeFinite.optional(),
          })
          .strict()
      )
      .max(300)
      .optional(),
  })
  .strict()

const qualityFinding = z
  .object({
    finding: z.enum([
      'APPOINTMENT_REQUESTED',
      'APPOINTMENT_REPORTED_BOOKED',
      'CANCELLATION_REQUESTED',
      'RESCHEDULE_REQUESTED',
      'CONFIRMATION_REQUESTED',
    ]),
    value: z.boolean(),
  })
  .strict()

export const callQualityAnalysisSchema = z
  .object({
    schemaVersion: z.literal(1),
    callRef,
    available: z.boolean(),
    summary: z.string().min(1).max(1_000).nullable(),
    providerSuccessEvaluation: z.enum(['PASS', 'FAIL', 'UNKNOWN']),
    structuredFindings: z.array(qualityFinding).max(5),
    runtimeActionAttempts: z
      .array(
        z.enum([
          'PATIENT_RESOLUTION',
          'PATIENT_CREATION',
          'SCHEDULING_DISCOVERY',
          'APPOINTMENT_BOOKING',
          'APPOINTMENT_CANCELLATION',
          'APPOINTMENT_RESCHEDULE',
          'APPOINTMENT_CONFIRMATION',
        ])
      )
      .max(7),
    authority: z
      .object({
        summary: z.literal('PROVIDER_DERIVED'),
        providerSuccessEvaluation: z.literal('PROVIDER_DERIVED'),
        structuredFindings: z.literal('PROVIDER_DERIVED'),
        runtimeActionAttempts: z.literal('FLIO_RUNTIME_EVIDENCE'),
        patientOutcome: z.literal('NOT_EVALUATED'),
        appointmentOutcome: z.literal('NOT_EVALUATED'),
      })
      .strict(),
  })
  .strict()

export type CallTimelineFacts = z.infer<typeof callTimelineSchema>
export type InteractionCall = z.infer<typeof interactionCallSchema>
export type InteractionCallPage = z.infer<typeof interactionCallPageSchema>
export type CallTranscript = z.infer<typeof callTranscriptSchema>
export type CallEvent = NonNullable<CallTranscript['events']>[number]
export type CallQualityAnalysis = z.infer<typeof callQualityAnalysisSchema>
